import { createStore } from '@/lib/tinyStore';
import type {
  MuteEntry, NotificationLevel, NotificationRule, NotificationSettings, QuietHours,
} from './types';

const DEFAULT_QUIET_HOURS: QuietHours = {
  enabled: false,
  start: '22:00',
  end: '07:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
  days: [],
  allowUrgent: true,
  allowCalls: true,
};

export const DEFAULT_SETTINGS: NotificationSettings = {
  inappEnabled: true,
  pushEnabled: true,
  soundEnabled: true,
  desktopBanner: true,
  showPreview: true,
  perKind: {},
  defaultTeamLevel: 'mentions',
  defaultChannelLevel: 'mentions',
  defaultChatLevel: 'all',
  quietHours: DEFAULT_QUIET_HOURS,
  respectDnd: true,
  mutes: [],
  rules: [],
  pushSupported: false,
  vapidPublicKey: null,
};

export const settingsStore = createStore<NotificationSettings>(DEFAULT_SETTINGS);

export const settingsActions = {
  hydrate(settings: NotificationSettings): void {
    settingsStore.set(settings);
  },

  patch(patch: Partial<NotificationSettings>): void {
    settingsStore.set((state) => ({ ...state, ...patch }));
  },

  setQuietHours(patch: Partial<QuietHours>): void {
    settingsStore.set((state) => ({ ...state, quietHours: { ...state.quietHours, ...patch } }));
  },

  setKindLevel(kind: keyof NotificationSettings['perKind'], level: NotificationLevel): void {
    settingsStore.set((state) => ({ ...state, perKind: { ...state.perKind, [kind]: level } }));
  },

  /** Replace the whole ledger with what the SERVER just returned. */
  setMutes(mutes: readonly MuteEntry[]): void {
    settingsStore.set((state) => ({ ...state, mutes }));
  },

  setRules(rules: readonly NotificationRule[]): void {
    settingsStore.set((state) => ({ ...state, rules }));
  },

  /** Mute a team / channel / conversation / person. `until` null = indefinitely. */
  mute(entry: MuteEntry): void {
    settingsStore.set((state) => {
      const mutes = state.mutes.filter(
        (row) => !(row.scopeKind === entry.scopeKind && row.scopeId === entry.scopeId),
      );
      return { ...state, mutes: [...mutes, entry] };
    });
  },

  unmute(scopeKind: MuteEntry['scopeKind'], scopeId: string): void {
    settingsStore.set((state) => ({
      ...state,
      mutes: state.mutes.filter((row) => !(row.scopeKind === scopeKind && row.scopeId === scopeId)),
    }));
  },

  upsertRule(rule: NotificationRule): void {
    settingsStore.set((state) => {
      const exists = state.rules.some((row) => row.id === rule.id);
      const rules = exists
        ? state.rules.map((row) => (row.id === rule.id ? rule : row))
        : [...state.rules, rule];
      return { ...state, rules };
    });
  },

  removeRule(id: string): void {
    settingsStore.set((state) => ({ ...state, rules: state.rules.filter((row) => row.id !== id) }));
  },
};

export const selectSettings = (state: NotificationSettings): NotificationSettings => state;
export const selectQuietHours = (state: NotificationSettings): QuietHours => state.quietHours;
export const selectMutes = (state: NotificationSettings): readonly MuteEntry[] => state.mutes;
export const selectRules = (state: NotificationSettings): readonly NotificationRule[] => state.rules;
export const selectSoundEnabled = (state: NotificationSettings): boolean => state.soundEnabled;
